import devCatalogRaw from '../assets/mockups/index.json';
import sampleBasePng from '../assets/mockups/sample-template/base.png';
import sampleEditBaseWebp from '../assets/mockups/sample-template/base.edit.webp';
import sampleDetailPreviewWebp from '../assets/mockups/sample-template/detailPreview.webp';
import sampleMaskPng from '../assets/mockups/sample-template/screenMask.png';
import sampleEditMaskWebp from '../assets/mockups/sample-template/screenMask.edit.webp';
import sampleOverlayPng from '../assets/mockups/sample-template/screenOverlay.png';
import sampleEditOverlayWebp from '../assets/mockups/sample-template/screenOverlay.edit.webp';
import samplePreviewWebp from '../assets/mockups/sample-template/preview.webp';
import samplePreviewLargeWebp from '../assets/mockups/sample-template/preview.large.webp';
import type { Category, DomainCategory, MockupTemplate, TemplateType } from '../types';
import { INDEX_URL } from './api';
import { normalizeTags } from './tags';

const DEV_ASSETS_BASE = 'mockup-local://assets/';
const DEV_BUNDLED_ASSETS: Record<string, string> = {
  'sample-template/base.png': sampleBasePng,
  'sample-template/base.edit.webp': sampleEditBaseWebp,
  'sample-template/detailPreview.webp': sampleDetailPreviewWebp,
  'sample-template/screenMask.png': sampleMaskPng,
  'sample-template/screenMask.edit.webp': sampleEditMaskWebp,
  'sample-template/screenOverlay.png': sampleOverlayPng,
  'sample-template/screenOverlay.edit.webp': sampleEditOverlayWebp,
  'sample-template/preview.webp': samplePreviewWebp,
  'sample-template/preview.large.webp': samplePreviewLargeWebp
};

export async function loadCatalog(indexUrlOverride?: string): Promise<MockupTemplate[]> {
  const targetIndexUrl = (indexUrlOverride || INDEX_URL).trim();

  if (targetIndexUrl) {
    try {
      const response = await fetch(targetIndexUrl, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Catalog request failed: ${response.status}`);
      }
      const payload = (await response.json()) as unknown;
      const parsed = parseCatalogPayload(payload, targetIndexUrl);
      if (parsed.length > 0) {
        return parsed;
      }
    } catch (error) {
      console.warn('[catalog] remote fetch failed, fallback to local', error);
    }
  }

  return parseCatalogPayload(devCatalogRaw, DEV_ASSETS_BASE);
}

export function parseCatalogPayload(payload: unknown, baseUrl: string): MockupTemplate[] {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload
    .filter((item) => !isUnsupportedTemplateProfile(item))
    .map((item) => normalizeTemplate(item, baseUrl))
    .filter((item): item is MockupTemplate => item !== null);
}

function isUnsupportedTemplateProfile(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') {
    return false;
  }
  const value = raw as Record<string, unknown>;
  // Flexible surfaces are authored and rendered by the browser web app.
  if (value.flexibleDisplay != null) return true;
  const screenProfile = value.screenProfile;
  if (screenProfile && typeof screenProfile === 'object') {
    const id = (screenProfile as { id?: unknown }).id;
    if (id === 'dual-screen' || id === 'triple-screen') return true;
  }
  const staticProfile = value.staticProfile;
  if (staticProfile && typeof staticProfile === 'object') {
    const id = (staticProfile as { id?: unknown }).id;
    if (id === 'static.dual-screen' || id === 'static.triple-screen') return true;
  }
  const motionProfile = value.motionProfile;
  return Boolean(
    motionProfile &&
    typeof motionProfile === 'object' &&
    ((motionProfile as { id?: unknown }).id === 'motion.dual-screen' ||
      (motionProfile as { id?: unknown }).id === 'motion.triple-screen')
  );
}

function normalizeTemplate(raw: unknown, baseUrl: string): MockupTemplate | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const value = raw as Record<string, unknown>;
  const id = toString(value.id);
  const name = toString(value.name);
  const category = normalizeCategory(value.category);
  const assetVersion = toOptionalString(value.updatedAt) || toOptionalString(value.createdAt);
  const previewVersion = toOptionalString(value.previewHash) || toOptionalString(value.previewUpdatedAt) || assetVersion;
  const templateType = normalizeTemplateType(value.templateType);
  const isVideo = toBoolean(value.isVideo);
  const base = resolveAssetUrl(value.base, baseUrl, assetVersion);
  const editBase = resolveOptionalAssetUrl(value.editBase, baseUrl, assetVersion);
  const mask = resolveAssetUrl(value.mask, baseUrl, assetVersion);
  const editMask = resolveOptionalAssetUrl(value.editMask, baseUrl, assetVersion);
  const mask2 = resolveOptionalAssetUrl(value.mask2, baseUrl, assetVersion);
  const editMask2 = resolveOptionalAssetUrl(value.editMask2, baseUrl, assetVersion);
  const overlay = resolveAssetUrl(value.overlay, baseUrl, assetVersion);
  const editOverlay = resolveOptionalAssetUrl(value.editOverlay, baseUrl, assetVersion);
  const reflectionMask = resolveOptionalAssetUrl(value.reflectionMask, baseUrl, assetVersion);
  const editReflectionMask = resolveOptionalAssetUrl(value.editReflectionMask, baseUrl, assetVersion);
  const reflectionBlurMask = resolveOptionalAssetUrl(value.reflectionBlurMask, baseUrl, assetVersion);
  const editReflectionBlurMask = resolveOptionalAssetUrl(value.editReflectionBlurMask, baseUrl, assetVersion);
  const screenBlurMask = resolveOptionalAssetUrl(value.screenBlurMask, baseUrl, assetVersion);
  const editScreenBlurMask = resolveOptionalAssetUrl(value.editScreenBlurMask, baseUrl, assetVersion);
  const preview = resolveAssetUrl(value.preview, baseUrl, previewVersion);
  const previewLarge = resolveOptionalAssetUrl(value.previewLarge, baseUrl, assetVersion);
  const detailPreview = resolveOptionalAssetUrl(value.detailPreview, baseUrl, assetVersion);
  const previewAtlasUrl = resolveOptionalAssetUrl(value.previewAtlasUrl, baseUrl, assetVersion);
  const atlasUrl = resolveOptionalAssetUrl(value.atlasUrl, baseUrl, assetVersion);
  const trackingUrl = resolveOptionalAssetUrl(value.trackingUrl, baseUrl, assetVersion);
  const backgroundMask = resolveOptionalAssetUrl(value.backgroundMask, baseUrl, assetVersion);
  const editBackgroundMask = resolveOptionalAssetUrl(value.editBackgroundMask, baseUrl, assetVersion);

  if (!id || !name || !category || !preview) {
    return null;
  }

  if (!isVideo && (!base || !mask || !overlay)) {
    return null;
  }

  const outputSize = toNumber(value.outputSize, 1920);
  const editSize = toOptionalNumber(value.editSize);
  const screenQuad = normalizeQuad(value.screenQuad, outputSize);

  return {
    id,
    name,
    tags: normalizeTags(value.tags),
    createdAt: toOptionalString(value.createdAt),
    updatedAt: toOptionalString(value.updatedAt),
    previewHash: toOptionalString(value.previewHash),
    previewUpdatedAt: toOptionalString(value.previewUpdatedAt),
    category,
    domainCategory: normalizeDomainCategory(value.domainCategory),
    templateType,
    sourceFilter: value.sourceFilter === 'CG' || value.sourceFilter === 'AI' ? value.sourceFilter : undefined,
    order: typeof value.order === 'number' ? value.order : undefined,
    isVideo,
    collectionId: toCollectionId(value.collectionId, templateType),
    transparency: toBoolean(value.transparency),
    reflectionEnabled: toBoolean(value.reflectionEnabled),
    published: value.published !== false,
    outputSize,
    editSize,
    base: base || undefined,
    editBase,
    mask: mask || undefined,
    editMask,
    mask2,
    editMask2,
    overlay: overlay || undefined,
    editOverlay,
    reflectionMask,
    editReflectionMask,
    reflectionBlurMask,
    editReflectionBlurMask,
    screenBlurMask,
    editScreenBlurMask,
    preview,
    previewLarge,
    detailPreview,
    previewAtlasUrl,
    atlasUrl,
    trackingUrl,
    fps: toOptionalNumber(value.fps),
    atlasLayout: value.atlasLayout === 'extended-6' || value.atlasLayout === 'standard-4' ? value.atlasLayout : undefined,
    atlasContract:
      value.atlasContract === 'screen-overlay-v1' || value.atlasContract === 'legacy-reflection-v1'
        ? value.atlasContract
        : undefined,
    screenBlurEnabled: isVideo
      ? (toBoolean(value.motionDynamicIslandToggle) ? false : value.screenBlurEnabled !== false)
      : undefined,
    motionDynamicIslandToggle: isVideo ? toBoolean(value.motionDynamicIslandToggle) : undefined,
    backgroundMask,
    editBackgroundMask,
    screenQuad,
    reflectionQuad: normalizeOptionalQuad(value.reflectionQuad, outputSize),
    safeRect: normalizeSafeRect(value.safeRect, outputSize),
    screenFit: value.screenFit === 'cover' ? 'cover' : 'contain',
    defaultExportMode: value.defaultExportMode === 'fit' ? 'fit' : 'crop'
  };
}

const VALID_CATEGORIES = new Set<string>([
  // App
  'Phone', 'Tablet', 'Laptop', 'Desktop', 'Clock',
  // Promo
  'Billboards', 'Presentation Screens', 'Digital Signage', 'Urban Scenes', 'Cafe Scenes', 'Outdoor Promo',
  // Brand
  'Stationery', 'Business Cards', 'Logo Placements', 'Identity Pack', 'Office Branding', 'Minimal Brand Mockups',
  // Legacy
  'Scenes'
]);

function normalizeCategory(value: unknown): Category | null {
  if (typeof value === 'string' && VALID_CATEGORIES.has(value)) {
    return value as Category;
  }
  return null;
}

function normalizeTemplateType(value: unknown): TemplateType {
  const normalized = String(value || '').trim().toUpperCase().replace(/_/g, '-');
  if (normalized === 'MOTION-AI' || normalized === 'MOTIONAI') {
    return 'MOTION-AI';
  }
  if (normalized === 'MOTION-CG' || normalized === 'MOTIONCG' || normalized === 'MOTION') {
    return 'MOTION-CG';
  }
  if (normalized === 'AI') {
    return 'AI';
  }
  return 'CG';
}

function normalizeDomainCategory(value: unknown): DomainCategory | undefined {
  if (value === 'App' || value === 'Promo' || value === 'Brand') {
    return value;
  }
  return undefined;
}

function normalizeQuad(value: unknown, outputSize: number): MockupTemplate['screenQuad'] {
  const fallback: MockupTemplate['screenQuad'] = defaultScreenQuad(outputSize);

  if (!Array.isArray(value) || value.length !== 4) {
    return fallback;
  }

  const points = value
    .map((point) => {
      if (!point || typeof point !== 'object') {
        return null;
      }
      const p = point as Record<string, unknown>;
      return {
        x: toNumber(p.x, 0),
        y: toNumber(p.y, 0)
      };
    })
    .filter((point): point is { x: number; y: number } => point !== null);

  return points.length === 4 ? points : fallback;
}

function normalizeOptionalQuad(value: unknown, outputSize: number): MockupTemplate['reflectionQuad'] {
  if (!Array.isArray(value) || value.length !== 4) {
    return undefined;
  }

  return normalizeQuad(value, outputSize);
}

function normalizeSafeRect(value: unknown, outputSize: number): MockupTemplate['safeRect'] {
  const fallback = defaultSafeRect(outputSize);

  if (!value || typeof value !== 'object') {
    return fallback;
  }

  const rect = value as Record<string, unknown>;
  return {
    x: clamp(toNumber(rect.x, fallback.x), 0, outputSize),
    y: clamp(toNumber(rect.y, fallback.y), 0, outputSize),
    w: clamp(toNumber(rect.w, fallback.w), 1, outputSize),
    h: clamp(toNumber(rect.h, fallback.h), 1, outputSize)
  };
}

function resolveAssetUrl(value: unknown, baseUrl: string, version?: string): string | null {
  const input = toString(value);
  if (!input) {
    return null;
  }

  if (baseUrl === DEV_ASSETS_BASE && DEV_BUNDLED_ASSETS[input]) {
    return DEV_BUNDLED_ASSETS[input];
  }

  try {
    const resolved = new URL(input, baseUrl);
    if (version) {
      resolved.searchParams.set('v', version);
    }
    return resolved.toString();
  } catch {
    return null;
  }
}

function resolveOptionalAssetUrl(value: unknown, baseUrl: string, version?: string): string | undefined {
  const resolved = resolveAssetUrl(value, baseUrl, version);
  return resolved || undefined;
}

function toString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toOptionalString(value: unknown): string | undefined {
  const normalized = toString(value);
  return normalized || undefined;
}

function toCollectionId(value: unknown, templateType: TemplateType): string {
  const id = toString(value);
  if (id) {
    return id === 'default-motion' && templateType === 'MOTION-CG' ? 'default-motion-cg' : id;
  }
  if (templateType === 'AI') {
    return 'default-ai';
  }
  if (templateType === 'MOTION-AI') {
    return 'default-motion-ai';
  }
  if (templateType === 'MOTION-CG') {
    return 'default-motion-cg';
  }
  return 'default-cg';
}

function toBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return false;
}

function toNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

function toOptionalNumber(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function defaultScreenQuad(outputSize: number): MockupTemplate['screenQuad'] {
  return [
    { x: Math.round(outputSize * 0.28125), y: Math.round(outputSize / 6) },
    { x: Math.round(outputSize * 0.71875), y: Math.round(outputSize / 6) },
    { x: Math.round(outputSize * 0.71875), y: Math.round(outputSize * 0.8333333333) },
    { x: Math.round(outputSize * 0.28125), y: Math.round(outputSize * 0.8333333333) }
  ];
}

function defaultSafeRect(outputSize: number): MockupTemplate['safeRect'] {
  return {
    x: Math.round(outputSize * 0.125),
    y: Math.round(outputSize * 0.125),
    w: Math.round(outputSize * 0.75),
    h: Math.round(outputSize * 0.75)
  };
}
